import React, { forwardRef } from "react";
import { Dumbbell, Flame } from "lucide-react";
import { fitTitleWithWrap } from "../lib/fitText";

/**
 * 1080×1080 shareable PR card. Same brand language as RankShareCard.
 * Expects: { exercise_name, weight, reps, e1rm, previous_e1rm, delta_e1rm, first_time }
 */
const PRShareCard = forwardRef(function PRShareCard({ pr, handle }, ref) {
    if (!pr) return null;
    const delta = pr.delta_e1rm || 0;
    // Card padding is 64 each side → usable width = 952. Exercise names
    // range from "SQUAT" (5 chars) to "BULGARIAN SPLIT SQUAT" (21 chars) to
    // "BARBELL ROMANIAN DEADLIFT" (25 chars). Auto-size + wrap to two lines
    // for the longest names so nothing bleeds off the card.
    const title = fitTitleWithWrap(pr.exercise_name || "", {
        maxWidth: 952,
        maxSize: 124,
        minSize: 64,
        wrapThreshold: 72,
    });
    return (
        <div
            ref={ref}
            data-testid="pr-share-card"
            style={{
                width: 1080,
                height: 1080,
                background: "#0a0a0a",
                color: "#ffffff",
                position: "relative",
                overflow: "hidden",
                fontFamily: '"Barlow Condensed","DM Sans",system-ui,sans-serif',
            }}
        >
            <div style={{
                position: "absolute", inset: 0,
                backgroundImage:
                    "repeating-linear-gradient(135deg, rgba(255,59,48,0.06) 0px, rgba(255,59,48,0.06) 2px, transparent 2px, transparent 26px)",
            }} />
            <div style={{
                position: "absolute", left: -180, top: -180, width: 720, height: 720,
                background: "radial-gradient(circle, rgba(255,59,48,0.42) 0%, rgba(255,59,48,0) 60%)", filter: "blur(8px)",
            }} />

            {/* Top brand */}
            <div style={{ position: "absolute", top: 60, left: 64, right: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Dumbbell size={36} color="#ff3b30" />
                    <span style={{ fontWeight: 900, fontSize: 38, letterSpacing: -0.5, textTransform: "uppercase" }}>
                        Hyper<span style={{ color: "#ff3b30" }}>gains</span>
                    </span>
                </div>
                <span style={{ fontSize: 16, letterSpacing: 4, textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
                    {pr.first_time ? "PR · First Logged" : "Personal Record"}
                </span>
            </div>

            {/* Center */}
            <div style={{ position: "absolute", left: 64, right: 64, top: 220, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, color: "#ff3b30", fontSize: 22, letterSpacing: 6, textTransform: "uppercase", fontWeight: 800 }}>
                    <Flame size={36} color="#ff3b30" />
                    New PR
                </div>
                <div style={{ marginTop: 24, fontWeight: 900, fontSize: title.size, lineHeight: 0.92, letterSpacing: -2, textTransform: "uppercase" }}>
                    {title.lines.map((line, i) => (
                        <div key={i}>{line}</div>
                    ))}
                </div>
                <div style={{ marginTop: 36, display: "flex", gap: 24 }}>
                    <Stat label="Weight" value={`${Math.round(pr.weight)}`} unit="lb / kg" />
                    <Stat label="Reps" value={`${pr.reps}`} unit="clean" />
                    <Stat label="Est. 1RM" value={`${Math.round(pr.e1rm)}`} unit="Epley" big />
                </div>
            </div>

            {/* Delta + tagline */}
            <div style={{ position: "absolute", left: 64, right: 64, bottom: 200, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <div>
                    <div style={{ fontSize: 16, letterSpacing: 4, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", fontWeight: 700 }}>
                        {pr.first_time ? "Baseline set" : "Improvement"}
                    </div>
                    <div style={{ marginTop: 6, fontWeight: 900, fontSize: 70, color: "#ff3b30", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                        {pr.first_time ? "✚" : `+${Math.round(delta)}`} <span style={{ fontSize: 28, color: "rgba(255,255,255,0.55)" }}>{pr.first_time ? "logged" : "lb on e1RM"}</span>
                    </div>
                </div>
                <div style={{ maxWidth: 420, textAlign: "right", fontStyle: "italic", fontSize: 28, color: "rgba(255,255,255,0.85)", lineHeight: 1.15 }}>
                    "Tension. Time. Truth."
                </div>
            </div>

            {/* Footer */}
            <div style={{ position: "absolute", left: 64, right: 64, bottom: 56, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 22, letterSpacing: 6, textTransform: "uppercase", fontWeight: 800, color: "rgba(255,255,255,0.65)" }}>
                    {handle ? handle : "Earned, not given."}
                </span>
                <span style={{ fontSize: 20, letterSpacing: 5, textTransform: "uppercase", color: "rgba(255,255,255,0.45)" }}>
                    hypergains.fit
                </span>
            </div>
        </div>
    );
});

function Stat({ label, value, unit, big }) {
    return (
        <div style={{
            border: big ? "2px solid #ff3b30" : "2px solid rgba(255,255,255,0.12)",
            background: big ? "rgba(255,59,48,0.08)" : "rgba(255,255,255,0.03)",
            padding: "20px 24px",
            minWidth: 200,
        }}>
            <div style={{ fontSize: 14, letterSpacing: 4, color: big ? "#ff3b30" : "rgba(255,255,255,0.55)", textTransform: "uppercase", fontWeight: 700 }}>{label}</div>
            <div style={{ marginTop: 6, fontWeight: 900, fontSize: 64, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
            <div style={{ marginTop: 2, fontSize: 16, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: 3 }}>{unit}</div>
        </div>
    );
}

export default PRShareCard;
